import { tracked } from '@glimmer/tracking';
import Ember from 'ember';
import { capitalize as capitalizeHelper } from '@ember/string';
import config from '../config/environment';
import _MomentService from 'ember-moment/services/moment';
import moment from 'moment-timezone';
import { task, timeout } from 'ember-concurrency';
import { dependentKeyCompat } from '@ember/object/compat';

// We import this file in moment.js, to avoid namespace collisions
// between our .ts service definitions and .js definitions from services from
// addons.
// This is to avoid the following warning:
//
// WARNING: Detected collisions between .js and .ts files of the same name.
// This can result in nondeterministic build output; see https://git.io/JvIwo for more information.

export default class MomentService extends _MomentService {
  @tracked currentTime: moment.Moment;

  constructor() {
    super(...arguments);

    this._setTimeZone();
    this._setDefaultFormat();
    this._setDefaultCalendarFormat();

    // Set currentTime synchronously here to make sure it's always a valid
    // moment() instance. If we rely on the e-c task, it will be null for one
    // tick of the runloop, which could cause weird things to happen.
    this.currentTime = this.moment();
    if (!Ember.testing) {
      this._tick.perform();
    }
  }

  @dependentKeyCompat
  get currentTimestamp() {
    return this.currentTime?.unix();
  }

  defaultFormat = config['ember-moment'].outputFormat;

  // For no apparent reason, the default ember-moment moment service
  // does not include this property.
  // Instead, every individual helper imports the config and makes a call to
  // Ember.get(config, 'moment.allowEmpty').
  // Making that call here instead saves us from duplicate code
  // when we want to create new helpers.
  // It's also consistent with the approach ember-moment uses
  // for the defaultFormat property.
  allowEmpty = config['ember-moment'].allowEmpty;

  // used by FormatTimestamp component
  // It passes:
  // - whether it want the text capitalized
  // - and the custom formatting function for the particular component:
  //     - detailedTimestamp computed property: doesn't pass a formatter
  //       - so the default formatter is used - timestampAsMoment.format()
  //     - LongTimestamp component: timestampAsMoment.calendar()
  //     - ShortDate component: momentService.shortCalendar(timestampAsMoment)
  //     - TimeAgo component: timestampAsMoment.fromNow();
  // The custom formatting function is applied *after* the timestampFormatter does
  // generic parsing and formatting needed for every timestamp:
  // - parses the unix timestamp
  // - calls momentService.moment(timestamp), which applies the timezone & locale
  // - then calls the custom formatting function, which accepts
  //   the timestampAsMoment and momentService as its arguments
  formatTimestamp(
    [timestamp]: [number],
    {
      capitalize = true,
      finalFormatter = function (timestampAsMoment: moment.Moment, _opts: any) {
        return timestampAsMoment.format();
      },
    } = {},
  ) {
    let timestampAsUnixMoment;

    if (timestamp) {
      // This is exactly how the ember-moment unix helper works
      timestampAsUnixMoment = moment.unix(timestamp);
    }

    let formattedTimestamp;
    let { allowEmpty } = this;

    if (!allowEmpty || timestamp) {
      // the service's moment function includes timestamp and locale information,
      // thanks to ember-moment
      let timestampAsMoment = this.moment(timestampAsUnixMoment ?? moment.invalid()); // lack of timestamp at this stage means we should display "invalid date"

      formattedTimestamp = finalFormatter(timestampAsMoment, this);

      if (capitalize) {
        formattedTimestamp = capitalizeHelper(formattedTimestamp);
      }
    }

    return formattedTimestamp ?? '—';
  }

  // used by ShortDate component via FormatTimestamp component
  shortCalendar(timestampAsMoment: moment.Moment, { alwaysShowTime = false }) {
    return timestampAsMoment.calendar(undefined, {
      lastDay: '[yesterday] [at] h:mm a',
      sameDay: '[today] [at] h:mm a',
      nextDay: '[tomorrow] [at] h:mm a',
      lastWeek: 'dddd [at] h:mm a',
      nextWeek: 'dddd [at] h:mm a',
      sameElse: alwaysShowTime ? 'MMM D, YYYY [at] h:mm a' : 'MMM D, YYYY',
    });
  }

  _setTimeZone() {
    let timeZone = 'America/Los_Angeles'; // Portland timezone - used for tests

    if (!Ember.testing) {
      // If not in the test environment,
      // set the timezone to the user's local timezone
      timeZone = moment.tz.guess();
    }

    // Set the timezone globally
    // (the moment helper needs this to be explicitly set,
    // even if it is just the browser default)
    this.setTimeZone(timeZone);
  }

  _setDefaultFormat() {
    // Sadly, ember-moment doesn't make use of moment's defaultFormat property
    // (http://momentjs.com/docs/#/displaying/format/ - bottom of this section)
    // When you set the ember-moment moment service's defaultFormat, the only
    // place that it's used is in the moment-format helper - it's sent as an
    // argument to it :/
    // https://github.com/stefanpenner/ember-moment/blob/master/addon/helpers/moment-format.js#L32
    moment.defaultFormat = this.defaultFormat;
  }

  // moment.calendar() is used by LongTimestamp component via FormatTimestamp component
  _setDefaultCalendarFormat() {
    moment.updateLocale('en', {
      calendar: {
        lastDay: '[yesterday] [at] h:mm:ss a [(]z[)]',
        sameDay: '[today] [at] h:mm:ss a [(]z[)]',
        nextDay: '[tomorrow] [at] h:mm:ss a [(]z[)]',
        lastWeek: this.defaultFormat,
        nextWeek: this.defaultFormat,
        sameElse: this.defaultFormat,
      },
    });
  }

  _tick = task({ drop: true }, async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // A minute seems often enough to update all the timestamps. We don't use
      // any relative timestamps with sub-minute accuracy so it should be fine,
      // I think.
      await timeout(60 * 1000);
      this.set('currentTime', this.moment());
    }
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    moment: MomentService;
  }
}
